import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { ProductProvider } from './context/ProductContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteProvider } from './context/SiteContext';
import { SiteTrackingProvider } from './context/SiteTrackingContext';
import { RedirectProvider, useRedirect } from './context/RedirectContext';
import SitePopupModal from './components/SitePopupModal/SitePopupModal';
import EngagementPopups from './components/EngagementPopups/EngagementPopups';

// Layout Components
import DocumentHead from './components/DocumentHead/DocumentHead';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import CartDrawer from './components/CartDrawer/CartDrawer';

// Page Components
import Home from './pages/Home';
import Shop from './components/Shop/Shop';
import ProductDetail from './components/ProductDetail/ProductDetail';
import Category from './components/Category/Category';
import { AuthProvider } from './context/AuthContext';
import SignIn from './components/SignIn/SignIn';
import Checkout from './components/Checkout/Checkout';
import Account from './components/Account/Account';
import { WishlistProvider } from './context/WishlistContext';
import Wishlist from './components/Wishlist/Wishlist';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import ShippingPolicy from './pages/ShippingPolicy';
import ReturnsPolicy from './pages/ReturnsPolicy';
import Contact from './pages/Contact';
import FAQs from './pages/FAQs';
import InfoPage from './pages/InfoPage';
import NotFound from './pages/NotFound';
import RedirectEmbed from './pages/RedirectEmbed';

function AppShell() {
  const { hideSiteChrome } = useRedirect();
  return (
    <div className="app">
      {!hideSiteChrome ? <Header /> : null}
      {!hideSiteChrome ? <CartDrawer /> : null}

      <main
        className={`main-content${hideSiteChrome ? ' main-content--redirect-embed' : ''}`}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/category/:id" element={<Category />} />
          <Route path="/category/:id/:subcategory" element={<Category />} />
          <Route path="/sale" element={<Shop />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/account" element={<Account />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/returns" element={<ReturnsPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route
            path="/rewards"
            element={
              <InfoPage title="Star Rewards">
                <p>
                  Earn rewards on every purchase. Create an account and shop to start earning Star Money. Program details and tier benefits
                  are coming soon—for now, enjoy shopping and check back for updates.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/stores"
            element={
              <InfoPage title="Stores">
                <p>
                  Find a store near you. Use the contact information in the footer for store-related questions, or shop online anytime.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/orders"
            element={
              <InfoPage title="Order history">
                <p>
                  Signed-in customers can track purchases from their account. Order lookup and full history will appear here in a future
                  update.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <InfoPage title="Reset password">
                <p>
                  If you forgot your password, contact customer support using the email or phone on our Contact page. Self-service reset will
                  be available soon.
                </p>
              </InfoPage>
            }
          />
          <Route
            path="/about"
            element={
              <InfoPage title="About us">
                <p>Learn more about our brand, mission, and values. Full company story coming soon.</p>
              </InfoPage>
            }
          />
          <Route
            path="/careers"
            element={
              <InfoPage title="Careers">
                <p>We’re always looking for great talent. Career listings and applications will be posted here soon.</p>
              </InfoPage>
            }
          />
          <Route
            path="/press"
            element={
              <InfoPage title="Press room">
                <p>Media inquiries and press resources will be available here. For urgent requests, use our Contact page.</p>
              </InfoPage>
            }
          />
          <Route
            path="/gift-cards"
            element={
              <InfoPage title="Gift cards">
                <p>Purchase and redeem gift cards online and in stores. Details and balance check coming soon.</p>
              </InfoPage>
            }
          />
          <Route
            path="/coupons"
            element={
              <InfoPage title="Coupons & offers">
                <p>Browse current promotions and coupon codes. New offers are added regularly—check back often.</p>
              </InfoPage>
            }
          />
          <Route path="/:slug" element={<RedirectEmbed />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {!hideSiteChrome ? <Footer /> : null}
    </div>
  );
}

function App() {
  return (
    <SiteProvider>
      <Router>
        <SiteTrackingProvider>
          <RedirectProvider>
            <AuthProvider>
              <WishlistProvider>
                <CartProvider>
                  <ProductProvider>
                    <ThemeProvider>
                      <DocumentHead />
                      <SitePopupModal />
                      <EngagementPopups />
                      <AppShell />
                    </ThemeProvider>
                  </ProductProvider>
                </CartProvider>
              </WishlistProvider>
            </AuthProvider>
          </RedirectProvider>
        </SiteTrackingProvider>
      </Router>
    </SiteProvider>
  );
}

export default App;
